import { useMemo } from 'react'
import { ApolloClient, gql, OperationVariables } from '@apollo/client'
import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { formatPrice, getPriceForCurrency } from '@/services/PriceFormatter'
import { GetProjectGoalsQuery, GetProjectGoalsQueryVariables, GetProjectMetricsQuery } from '@/types/codegen-federation'
import { getIsTheatrical } from '@/utils/SpecialProjectUtils'

export const PROJECT_GOALS_QUERY = gql`
  query getProjectGoals($slug: String!) {
    projectBySlug(projectSlug: $slug) {
      activeGoalsGroup {
        activeGoal {
          amount
          backers
          groupName
          id
          name
          percentRaised
          projectName
          projectSlug
          isActive
          raised
        }
        goals {
          achievedDate
          amount
          backers
          id
          isActive
          name
          percentRaised
          raised
          sequence
          startDate
          timeLeft
          timeLeftUnits
          units
        }
      }
    }
  }
`

const PROJECT_VIEWS_QUERY = gql`
  query getProjectMetrics($projectSlug: String!) {
    project(slug: $projectSlug) {
      id
      name
    }
    projectMetrics(projectSlug: $projectSlug) {
      viewsAtLeastThirtySecondsLong
    }
  }
`

interface UseGoalProps {
  slug?: string
  client?: ApolloClient
  goalId?: string
}

export interface UseGoalValues {
  loading: boolean
  error?: ErrorLike
  raisedRaw?: number | null
  raised?: string | null
  cost?: string | null
  costRaw?: number | null
  percentProgress?: number | null
  isActive?: boolean | null
  backers?: string | null
  groupName?: string | null
  name?: string | null
  refetch?: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<ApolloClient.QueryResult<GetProjectGoalsQuery>> | null
}

export const useGoal = ({ slug, client, goalId }: UseGoalProps): UseGoalValues => {
  const { data, loading, error, refetch } = useQuery<GetProjectGoalsQuery, GetProjectGoalsQueryVariables>(
    PROJECT_GOALS_QUERY,
    {
      variables: {
        slug: slug ?? '',
      },
      skip: !slug,
      client,
      errorPolicy: 'all',
    },
  )

  const currency = 'USD'
  const locale = 'en-US'

  const goalData = goalId
    ? data?.projectBySlug?.activeGoalsGroup?.goals?.find((goal) => goal?.id === goalId)
    : data?.projectBySlug?.activeGoalsGroup?.activeGoal

  const {
    amount = 0,
    backers: unsafeBackers,
    percentRaised: unsafePercentRaised,
    raised: unsafeRaised,
    name,
    isActive,
  } = goalData || {}

  const groupName = data?.projectBySlug?.activeGoalsGroup?.activeGoal?.groupName ?? undefined

  const raised = unsafeRaised ?? 0
  const percentRaised = unsafePercentRaised ?? 0
  const backers = unsafeBackers ?? 0

  const showTicketsNotDollars = slug && getIsTheatrical(slug)
  const ticketPrice = 14

  const formattedRaised = showTicketsNotDollars
    ? new Intl.NumberFormat(locale).format(getPriceForCurrency(raised, currency) / ticketPrice)
    : formatPrice(raised, { currency, includeDecimals: false, locale })
  const formattedCost = showTicketsNotDollars
    ? new Intl.NumberFormat(locale).format(getPriceForCurrency(amount, currency) / ticketPrice)
    : formatPrice(amount, { currency, includeDecimals: false, locale })
  const formattedPercent = percentRaised * 100
  const formattedBackers = new Intl.NumberFormat(locale).format(backers)

  return useMemo(
    (): UseGoalValues =>
      !slug
        ? {
            loading: false,
          }
        : {
            loading,
            error,
            isActive,
            raisedRaw: unsafeRaised,
            raised: formattedRaised,
            cost: formattedCost,
            costRaw: amount,
            percentProgress: formattedPercent,
            backers: formattedBackers,
            groupName,
            name,
            refetch,
          },
    [
      slug,
      loading,
      error,
      isActive,
      unsafeRaised,
      formattedRaised,
      formattedCost,
      amount,
      formattedPercent,
      formattedBackers,
      groupName,
      name,
      refetch,
    ],
  )
}

export const useViewsGoal = ({ projectSlug, client }: { projectSlug: string; client?: ApolloClient }) => {
  const { data, loading, error } = useQuery<GetProjectMetricsQuery>(PROJECT_VIEWS_QUERY, {
    variables: {
      projectSlug,
    },
    skip: !projectSlug,
    client,
    errorPolicy: 'all',
  })

  const views =
    data?.projectMetrics
      ?.map((metric) => metric?.viewsAtLeastThirtySecondsLong ?? 0)
      .reduce((acc: number, curr: number) => acc + curr, 0) ?? 0

  return { views, loading, error }
}
